import {Injectable} from '@angular/core';

declare var webkitSpeechRecognition: any;

@Injectable({
  providedIn: 'root'
})
export class SpeechToTextService {
  private messages: any = {
    start: {
      msg: 'Speech to text.',
      class: 'alert-success'
    },
    allow: {
      msg: 'Click on the microphone icon and begin speaking.',
      class: 'alert-success'
    },
    speak_now: {
      msg: 'Speak now.',
      class: 'alert-success'
    },
    no_speech: {
      msg: 'No speech was detected. You may need to adjust your <a href="//support.google.com/chrome/answer/2693767" target="_blank">microphone settings</a>.',
      class: 'alert-danger'
    },
    no_microphone: {
      msg: 'No microphone was found. Ensure that a microphone is installed and that <a href="//support.google.com/chrome/answer/2693767" target="_blank">microphone settings</a> are configured correctly.',
      class: 'alert-danger'
    },
    denied: {
      msg: 'Permission to use microphone was denied.',
      class: 'alert-danger'
    },
    blocked: {
      msg: 'Permission to use microphone is blocked. To change, go to chrome://settings/content/microphone',
      class: 'alert-danger'
    },
    upgrade: {
      msg: 'Web Speech API is not supported by this browser. It is only supported by <a href="//www.google.com/chrome">Chrome</a> version 25 or later on desktop and Android mobile.',
      class: 'alert-danger'
    },
    stop: {
      msg: 'Stop listening, click on the microphone icon to restart',
      class: 'alert-success'
    },
    copy: {
      msg: 'Content copy to clipboard successfully.',
      class: 'alert-success'
    },
  };
  private recognition: any;
  private recognizing?: boolean;
  private ignoreOnend?: boolean;
  private startTimestamp: any;
  public finalTranscript: string;

  onUpdateInfo: any;
  onUpdateSpeechText: any;
  onUpdateImage: any;

  private twoLine = /\n\n/g;
  private oneLine = /\n/g;
  private firstChar = /\S/;

  public allowSpeech: boolean;

  language = 'el-GR';

  setGreekLanguage() {
    this.language = 'el-GR';
    if (this.recognizing) {
      this.stop();
      setTimeout(() => {
        this.start(this.finalTranscript);
      }, 500);
    }
  }

  setEnglishLanguage() {
    this.language = 'en-US';
    if (this.recognizing) {
      this.stop();
      setTimeout(() => {
        this.start(this.finalTranscript);
      }, 500);
    }
  }

  constructor() {
    this.allowSpeech = false;
    this.finalTranscript = '';
  }

  public getSpeechRecognitionInstance() {
    return new webkitSpeechRecognition();
  }

  public supportSpeech(): boolean {
    return (typeof webkitSpeechRecognition !== 'undefined' && webkitSpeechRecognition !== null);
  }

  public init(): void {
    if (!this.supportSpeech()) {
      this.upgrade();
    } else {
      this.updateInfoText('start');
      this.recognition = this.getSpeechRecognitionInstance();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;

      const that = this;
      this.recognition.onstart = () => {
        that.recognizing = true;
        that.updateInfoText('speak_now');
        that.updateImageSrc('assets/speech/mic-animation.gif');
      };

      this.recognition.onerror = (event: any) => {
        if (event.error === 'no-speech') {
          that.updateImageSrc('assets/speech/speech.webp');
          that.updateInfoText('no_speech');
          that.ignoreOnend = true;
        }
        if (event.error === 'audio-capture') {
          that.updateImageSrc('assets/speech/speech.webp');
          that.updateInfoText('no_microphone');
          that.ignoreOnend = true;
        }
        if (event.error === 'not-allowed') {
          if (event.timeStamp - that.startTimestamp < 100) {
            that.updateInfoText('blocked');
          } else {
            that.updateInfoText('denied');
          }
          that.ignoreOnend = true;
        }
      };

      this.recognition.onend = () => {
        that.recognizing = false;
        if (that.ignoreOnend) {
          return;
        }
        that.updateImageSrc('assets/speech/speech-slash.webp');
        if (!that.finalTranscript) {
          that.updateInfoText('start');
          return;
        }
        that.updateInfoText('stop');
        /* if (window.getSelection) {
           window.getSelection().removeAllRanges();
           const range = document.createRange();
           range.selectNode(that.finalSpan);
           window.getSelection().addRange(range);
         }*/
      };

      this.recognition.onresult = (event: any) => {
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            that.finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }
        that.finalTranscript = that.capitalize(that.finalTranscript);
        that.showMessage(that.finalTranscript, interimTranscript);
      };
    }
  }


  public started(): boolean {
    return this.recognizing ?? false;
  }

  public start(text = '') {
    if (this.supportSpeech()) {
      if (this.recognizing) {
        this.stop();
      } else {
        this.recognition.lang = this.language;
        this.finalTranscript = text;
        this.recognition.start();
        this.ignoreOnend = false;
        this.showMessage(text, '');
        this.updateImageSrc('assets/speech/speech.webp');
        this.updateInfoText('allow');
        this.startTimestamp = new Date().getTime();
      }
    }
  }

  public stop() {
    if (this.supportSpeech() && this.recognition) {
      this.recognition.stop();
      this.updateInfoText('allow');
      this.updateImageSrc('assets/speech/speech.webp');
      this.ignoreOnend = true;
      this.recognizing = false;
    }
  }

  public clearText() {
    this.finalTranscript = '';
    this.showMessage('', '');
  }

  public updateImageSrc(imageSrc: any) {
    if (this.onUpdateImage) {
      this.onUpdateImage(imageSrc);
    }
  }

  public updateInfoText(s: string) {
    if (s) {
      const message = this.messages[s] ?? '';
      if (this.onUpdateInfo) {
        this.onUpdateInfo(message.msg, ['alert', message.class]);
      }
    } else {
      if (this.onUpdateInfo) {
        this.onUpdateInfo('', ['d-none']);
      }
    }
  }

  public upgrade() {
    if (this.onUpdateImage) {
      this.onUpdateImage('assets/speech/speech-slash.webp');
    }
    this.updateInfoText('upgrade');
  }

  public showMessage(finalTranscript: string, interimTranscript: string) {
    if (this.onUpdateSpeechText) {
      this.onUpdateSpeechText(finalTranscript, interimTranscript);
    }
  }

  // Tools:

  public linebreak(s: string): string {
    if (s) {
      return s.replace(this.twoLine, '<p></p>').replace(this.oneLine, '<br>');
    } else {
      return '';
    }
  }

  public capitalize(s: string) {
    return s.replace(this.firstChar, (m: string) => {
      return m.toUpperCase();
    });
  }
}
